/* Chia lại khách khi Telesales được giao không tương tác (gọi tổng đài / chat) quá thời gian cài đặt */
import dayjs from 'dayjs';
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import type { CustomerTagItem } from '../../../api/CustomerTag';
import { DAY, HOUR, MIN, activeAppt, dur, shortTag, staffOf, tagOf, type LeadLocal } from './logic';
import { staffInGroup, type GroupCfg, type GroupKey } from './groups';

export type IdleUnit = 'min' | 'hour' | 'day';
export type Channel = 'call' | 'chat';
export interface ReassignCfg {
  enabled: boolean;
  /** auto: tự chia khi phát hiện · suggest: chỉ đề xuất, Người vận hành bấm chia */
  mode: 'auto' | 'suggest';
  idle: number;
  unit: IdleUnit;
  /** Loại tương tác được tính */
  channels: Channel[];
  /** Mã S áp dụng (phần mã ngắn: Mới, S0...) */
  stages: string[];
  /** Chỉ đếm thời gian trong giờ làm việc */
  workHours: boolean;
  startH: number;
  endH: number;
  /** Chia luôn khách chưa có Telesales */
  includeUnassigned: boolean;
  strategy: 'least_load' | 'round_robin';
  /** id Telesales được nhận khách; trống = tất cả */
  pool: number[];
  /** Mỗi khách chia lại tối đa N lần, sau đó báo Người vận hành */
  maxTimes: number;
}

export const DEFAULT_REASSIGN: ReassignCfg = {
  enabled: true,
  mode: 'suggest',
  idle: 30,
  unit: 'min',
  channels: ['call', 'chat'],
  stages: ['Mới', 'S0', 'S1', 'S2', 'S3', 'S4'],
  workHours: true,
  startH: 8,
  endH: 21,
  includeUnassigned: true,
  strategy: 'least_load',
  pool: [],
  maxTimes: 2,
};

export const UNIT_LABEL: Record<IdleUnit, string> = { min: 'phút', hour: 'giờ', day: 'ngày' };
const UNIT_MS: Record<IdleUnit, number> = { min: MIN, hour: HOUR, day: DAY };
export const thresholdMs = (c: ReassignCfg) => Math.max(1, c.idle) * UNIT_MS[c.unit];
export const thresholdText = (c: Pick<ReassignCfg, 'idle' | 'unit'>) => `${c.idle} ${UNIT_LABEL[c.unit]}`;

/** Thời gian (ms) nằm trong giờ làm việc giữa from và to */
export function workingMs(from: number, to: number, c: Pick<ReassignCfg, 'workHours' | 'startH' | 'endH'>) {
  if (to <= from) return 0;
  if (!c.workHours) return to - from;
  let total = 0;
  let day = dayjs(from).startOf('day');
  while (day.valueOf() < to) {
    const s = Math.max(from, day.hour(c.startH).valueOf());
    const e = Math.min(to, day.hour(c.endH).valueOf());
    if (e > s) total += e - s;
    day = day.add(1, 'day');
  }
  return total;
}

/** Lần tương tác gần nhất của nhân viên với khách (theo các kênh được tính) */
export const lastInteraction = (l: LeadLocal | undefined, channels: Channel[], r?: ChannelCustomerItem) => {
  const local = l?.touches.find((t) => (t.kind === 'call' && channels.includes('call')) || (t.kind === 'msg' && channels.includes('chat')))?.at ?? 0;
  // Nhân viên tương tác ở máy khác: lấy thêm giờ server báo
  const call = r?.last_call && channels.includes('call') ? dayjs(r.last_call.at).valueOf() : 0;
  const msg = r?.last_msg_at && channels.includes('chat') ? dayjs(r.last_msg_at).valueOf() : 0;
  return Math.max(local, call, msg) || undefined;
};

export interface IdleInfo {
  /** Thuộc diện theo dõi (đúng mã S, không có việc hẹn trước) */
  eligible: boolean;
  /** Thời gian không tương tác đã tính (ms, theo giờ làm việc nếu bật) */
  idle: number;
  threshold: number;
  since: number;
  staff: { id: number; name: string } | null;
}

export function idleInfo(r: ChannelCustomerItem, l: LeadLocal | undefined, tags: CustomerTagItem[], c: ReassignCfg, now: number): IdleInfo {
  const staff = staffOf(r, l);
  const threshold = thresholdMs(c);
  const code = shortTag(tagOf(r, l, tags)?.name) || 'Mới';
  const created = r.created_at ? dayjs(r.created_at).valueOf() : now;
  const since = Math.max(l?.assignAt ?? created, lastInteraction(l, c.channels, r) ?? 0);
  const eligible = c.enabled
    && c.stages.some((s) => s.toUpperCase() === code.toUpperCase())
    && (!!staff || c.includeUnassigned)
    // Đã hẹn gọi lại / có lịch hẹn → nhân viên đang có kế hoạch, không chia lại
    && !(l?.callback && l.callback > now)
    && !activeAppt(l, now);
  return { eligible, idle: workingMs(since, now, c), threshold, since, staff };
}

export interface StaleLead { r: ChannelCustomerItem; info: IdleInfo; blocked: boolean }

export const findStale = (rows: ChannelCustomerItem[], leads: Record<number, LeadLocal>, tags: CustomerTagItem[], c: ReassignCfg, now: number): StaleLead[] =>
  rows
    .map((r) => ({ r, info: idleInfo(r, leads[r.id], tags, c, now), blocked: (leads[r.id]?.reassignCount ?? 0) >= c.maxTimes }))
    .filter((x) => x.info.eligible && x.info.idle >= x.info.threshold)
    .sort((a, b) => b.info.idle - a.info.idle);

export interface Staff { id: number; name: string }

/** Chia theo nhóm dịch vụ: nhóm của từng khách + cài đặt nhóm / tỉ lệ của telesale */
export interface GroupRule { cfg: GroupCfg; groupOf: (r: ChannelCustomerItem) => GroupKey | undefined }

/**
 * Chọn người nhận cho danh sách khách: ít khách nhất (theo số khách đang giữ) hoặc xoay vòng.
 * Không giao lại cho chính người đang giữ khách. Có `groups` thì chỉ giao cho người cùng nhóm dịch vụ với khách
 * (không ai cùng nhóm thì để nguyên, không đẩy sang nhóm khác); bật bù số thì "ít khách" tính theo tỉ lệ yêu cầu.
 */
export function planReassign(items: StaleLead[], staffs: Staff[], c: Pick<ReassignCfg, 'pool' | 'strategy'>, loads: Record<number, number>, rr: number, groups?: GroupRule) {
  const pool = staffs.filter((s) => !c.pool.length || c.pool.includes(s.id));
  const load = { ...loads };
  let idx = rr;
  const plan: { r: ChannelCustomerItem; from: Staff | null; to: Staff; idle: number }[] = [];
  items.filter((x) => !x.blocked).forEach(({ r, info }) => {
    const g = groups?.groupOf(r);
    const cands = pool.filter((s) => s.id !== info.staff?.id && (!groups || staffInGroup(groups.cfg, s.id, g)));
    if (!cands.length) return;
    // Bù số: số khách đang giữ chia cho tỉ lệ yêu cầu (người 40% nhận gấp đôi người 20%); người chưa đặt tỉ lệ lấy mức trung bình
    const ratios = cands.map((s) => groups?.cfg.ratio[String(s.id)] ?? 0).filter((x) => x > 0);
    const avg = ratios.length ? ratios.reduce((a, b) => a + b, 0) / ratios.length : 1;
    const weighted = (s: Staff) => (load[s.id] ?? 0) / (groups?.cfg.compensate ? groups.cfg.ratio[String(s.id)] || avg : 1);
    let to: Staff;
    if (c.strategy === 'round_robin') {
      to = cands[idx % cands.length];
      idx += 1;
    } else {
      to = [...cands].sort((a, b) => weighted(a) - weighted(b))[0];
    }
    load[to.id] = (load[to.id] ?? 0) + 1;
    if (info.staff) load[info.staff.id] = Math.max(0, (load[info.staff.id] ?? 0) - 1);
    plan.push({ r, from: info.staff, to, idle: info.idle });
  });
  return { plan, rr: idx };
}

export const idleText = (ms: number) => dur(ms);

/* ================= Chat · Trực page ================= */

/** Kênh chat (theo nguồn khách) */
export const CHAT_CHANNELS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'zalo', label: 'Zalo' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'instagram', label: 'Instagram' },
];

export interface ChatReassignCfg {
  enabled: boolean;
  mode: 'auto' | 'suggest';
  /** Khách nhắn mà chưa được trả lời quá N đơn vị → chia lại */
  idle: number;
  unit: IdleUnit;
  channels: string[];
  workHours: boolean;
  startH: number;
  endH: number;
  includeUnassigned: boolean;
  strategy: 'least_load' | 'round_robin';
  /** id Trực page được nhận chat; trống = tất cả */
  pool: number[];
  maxTimes: number;
}

export const DEFAULT_CHAT_REASSIGN: ChatReassignCfg = {
  enabled: true,
  mode: 'suggest',
  idle: 5,
  unit: 'min',
  channels: CHAT_CHANNELS.map((c) => c.value),
  workHours: true,
  startH: 8,
  endH: 21,
  includeUnassigned: true,
  strategy: 'least_load',
  pool: [],
  maxTimes: 2,
};

/** Trực page đang phụ trách chat: chia lại trên máy → in_charge_staff từ API */
export const chatStaffOf = (r: ChannelCustomerItem, l?: LeadLocal) =>
  l?.chatAssign ? { ...l.chatAssign, local: true } : r.in_charge_staff ? { id: r.in_charge_staff.id, name: r.in_charge_staff.name, local: false } : null;

/** Thời điểm tin khách đầu tiên chưa được trả lời (sau tin nhân viên gần nhất) */
export const chatWaitingSince = (l?: LeadLocal) => {
  const msgs = l?.msgs ?? [];
  const lastOut = msgs.reduce((m, x) => (x.from === 'n' ? Math.max(m, x.at) : m), -Infinity);
  return msgs.filter((x) => x.from === 'k' && x.at > lastOut).sort((a, b) => a.at - b.at)[0]?.at;
};

export function chatIdleInfo(r: ChannelCustomerItem, l: LeadLocal | undefined, c: ChatReassignCfg, now: number): IdleInfo {
  const staff = chatStaffOf(r, l);
  const waiting = chatWaitingSince(l);
  const src = (r.source || r.connections?.[0]?.source || '').toLowerCase();
  // Sau khi chia lại, người mới được tính lại thời gian từ lúc nhận
  const since = Math.max(waiting ?? now, l?.chatAssignAt ?? 0);
  const eligible = c.enabled && !!waiting
    && (!src || !CHAT_CHANNELS.some((x) => x.value === src) || c.channels.includes(src))
    && (!!staff || c.includeUnassigned);
  return { eligible, idle: workingMs(since, now, c), threshold: Math.max(1, c.idle) * UNIT_MS[c.unit], since, staff };
}

export const findChatStale = (rows: ChannelCustomerItem[], leads: Record<number, LeadLocal>, c: ChatReassignCfg, now: number): StaleLead[] =>
  rows
    .map((r) => ({ r, info: chatIdleInfo(r, leads[r.id], c, now), blocked: (leads[r.id]?.chatReassignCount ?? 0) >= c.maxTimes }))
    .filter((x) => x.info.eligible && x.info.idle >= x.info.threshold)
    .sort((a, b) => b.info.idle - a.info.idle);
